import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { dueBadge, moneyDisplay } from "@icraftx/shared/v1";
import { Button } from "@/components/ui/button";
import { AxisProgress } from "@/modules/core/components/AxisProgress";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import {
  DueBadge,
  FilterPartyName,
} from "@/modules/core/components/DueBadge";
import {
  togglePartyFilter,
  type PartyFilter,
} from "@/modules/core/list-cell-actions";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { ToPayRow } from "@/shared/api/purchasing";

const columnHelper = createColumnHelper<DeskTableFeatures, ToPayRow>();

export type ToPayCellActions = {
  partyFilter: PartyFilter | null;
  setPartyFilter: (next: PartyFilter | null) => void;
};

export function toPayColumns(
  companySlug: string,
  actions: ToPayCellActions,
) {
  return columnHelper.columns([
    columnHelper.accessor("number", {
      id: "number",
      header: "Number",
      size: 120,
      cell: (info) => {
        const row = info.row.original;
        const to =
          row.kind === "purchase_invoice"
            ? `/c/${companySlug}/purchasing/invoices/${row.id}`
            : `/c/${companySlug}/purchasing/orders/${row.id}`;
        return <DocNumberLink to={to}>{info.getValue()}</DocNumberLink>;
      },
    }),
    columnHelper.accessor("supplierName", {
      id: "supplierName",
      header: "Supplier",
      size: 180,
      cell: (info) => {
        const row = info.row.original;
        return (
          <FilterPartyName
            name={info.getValue()}
            onClick={() =>
              actions.setPartyFilter(
                togglePartyFilter(actions.partyFilter, {
                  id: row.supplierId,
                  name: row.supplierName,
                }),
              )
            }
          />
        );
      },
    }),
    columnHelper.display({
      id: "receivePct",
      header: "Received",
      size: 110,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Received"
            format="qty"
            current={r.qtyReceived}
            max={r.qtyOrdered}
          />
        );
      },
    }),
    columnHelper.display({
      id: "payPct",
      header: "Paid",
      size: 130,
      enableSorting: false,
      meta: { align: "right" },
      cell: ({ row }) => {
        const r = row.original;
        return (
          <AxisProgress
            axis="Paid"
            format="money"
            current={r.amountPaid}
            max={r.total}
          />
        );
      },
    }),
    columnHelper.accessor("outstanding", {
      id: "outstanding",
      header: "Outstanding",
      size: 120,
      meta: { align: "right" },
      cell: (info) => (
        <span className="font-mono tabular-nums">
          {moneyDisplay(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("paymentDueOn", {
      id: "paymentDueOn",
      header: "Due",
      size: 120,
      cell: (info) => {
        const badge = dueBadge(info.getValue() ?? null);
        return badge.label ? <DueBadge badge={badge} /> : "—";
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 88,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const r = row.original;
        const to =
          r.kind === "purchase_invoice"
            ? `/c/${companySlug}/purchasing/invoices/${r.id}`
            : `/c/${companySlug}/purchasing/orders/${r.id}`;
        return (
          <Button variant="outline" asChild size="sm">
            <Link to={to} className="no-underline">
              Pay
            </Link>
          </Button>
        );
      },
    }),
  ]);
}
